/**
 * Filters an object by removing entries with undefined, null, or empty string values.
 *
 * @param obj - The input object to be filtered.
 * @returns A new object with only non-empty values.
 * @template T - The type of the input object.
 */
export function getNonEmptyValuesFromObject<T>(obj: T): T {
  if (!obj) return {} as T;
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([_, value]) => value !== undefined && value !== null && value !== ''
    )
  ) as T;
}

/**
 * Checks if the value of a given key in an object has changed.
 *
 * @param {string} key - The key to check for in the object.
 * @param {string | number | undefined} value - The value to compare with the object's key value.
 * @param {Record<string, unknown> | null | undefined} object - The object to check in.
 * @returns {boolean} - Returns true if the value of the key in the object has changed or if the key does not exist in the object, false otherwise.
 */
export const isParameterValueChanged = <T extends object>(
  key: string,
  value: string | number | undefined,
  object: T
): boolean => {
  const record = object as Record<string, unknown>;
  if (!record || record[key] === undefined) {
    return false;
  }
  return record[key] !== value;
};

/**
 * Removes a set of keys from a given object.
 *
 * @template T - The type of the input object.
 * @param {string[]} keysToRemove - The array of keys to be removed from the object.
 * @param {T} inputObject - The input object from which keys will be removed.
 * @returns {T} - A new object of the same type as the input object, but without the keys specified in keysToRemove.
 */
export const removeKeysFromObject = <T extends object>(
  keysToRemove: (keyof T)[],
  inputObject: T
): Partial<T> => {
  if (!inputObject) {
    return {} as T;
  }
  const record = inputObject as Record<string, unknown>;
  return Object.keys(record).reduce((result: Partial<T>, key: string) => {
    if (!keysToRemove.includes(key as keyof T)) {
      result[key as keyof T] = record[key] as T[keyof T];
    }
    return result;
  }, {} as Partial<T>) as T;
};

const identity = (x: string) => x;

interface ParseOptions {
  keyValueSeparator: string;
  paramsSeparator: string;
  parseKey?: (key: string) => string;
}

/**
 * Parses a string into a key-value pair object.
 *
 * @param {string} string - The string to be parsed.
 * @param {Object} options - An object containing the separators and a key parsing function.
 * @param {string} options.keyValueSeparator - The separator between keys and values in the string.
 * @param {string} options.paramsSeparator - The separator between different key-value pairs in the string.
 * @param {Function} [options.parseKey=identity] - An optional function to parse the keys. Defaults to an identity function.
 * @returns {Record<string, string>} - An object with key-value pairs parsed from the input string.
 */
export const parseStringToParams = (
  string: string,
  { keyValueSeparator, paramsSeparator, parseKey = identity }: ParseOptions
): Record<string, string> =>
  string.split(paramsSeparator).reduce((obj, str) => {
    const [key, value] = str.split(keyValueSeparator);
    if (key && value) {
      obj[parseKey(key)] = value;
    }
    return obj;
  }, {});

/**
 * Reverses the key-value pairs in a given dictionary.
 *
 * @template D - The type of the input dictionary.
 * @template S - The type of the initial object.
 * @param {D} dictionary - The input dictionary whose key-value pairs are to be reversed.
 * @param {Partial<S>} initialObject - An optional initial object to which the reversed key-value pairs will be added. Defaults to an empty object.
 * @returns {Partial<S>} - An object with the reversed key-value pairs from the input dictionary. If an initial object was provided, the reversed key-value pairs will be added to it.
 */
export function reverseDictionary<
  D extends Record<string, string>,
  S extends Record<string, string>,
>(dictionary: D, initialObject: Partial<S> = {}): Partial<S> {
  return Object.keys(dictionary).reduce((result, key) => {
    const value = dictionary[key];
    if (value) {
      (result as Record<string, string>)[value] = key;
    }
    return result;
  }, initialObject);
}

/**
 Merges two objects deeply, combining their properties recursively.
 If a property in the source object is an object, it will be merged with the corresponding property in the target object.
 If a property in the source object is not an object, it will overwrite the corresponding property in the target object.

 @template T - The type of the target object.
 @template T - The type of the source object.
 @param {T} target - The target object to merge into.
 @param {T} source - The source object to merge from.
 @returns {T} - A new object containing the merged properties of both target and source. */
export const deepMerge = <T extends Record<string, unknown>>(
  target: T,
  source: T
): T => {
  const result = { ...target };

  for (const key in source) {
    if (
      source[key] &&
      typeof source[key] === 'object' &&
      !Array.isArray(source[key])
    ) {
      result[key] = deepMerge(
        (target[key] as Record<string, unknown>) || {},
        source[key] as Record<string, unknown>
      ) as T[typeof key];
    } else {
      result[key] = source[key];
    }
  }

  return result;
};
